import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Leaf, Loader2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const Center = ({ children, renalife }) => (
  <div
    className={`min-h-screen flex items-center justify-center px-5 ${renalife ? "" : "bg-[#050505] grid-texture"}`}
    style={renalife ? { background: "linear-gradient(160deg, #022C22 0%, #064E3B 60%, #022C22 100%)" } : undefined}
  >
    {children}
  </div>
);

export default function PinCampaign() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pinType, setPinType] = useState("standard");
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [saving, setSaving] = useState(false);

  const loadSession = async () => {
    try {
      const { data } = await api.get("/pin/session");
      // Si ya completó sus datos, lo mandamos al panel completo.
      if (data.ready) {
        await checkAuth();
        navigate("/dashboard", { replace: true });
        return;
      }
      setPinType(data.pin_type || "standard");
      setName(data.name && data.name !== "Invitado" ? data.name : "");
      setWhatsapp(data.whatsapp || "");
      setLoading(false);
    } catch (err) {
      toast.error("Necesitas un PIN válido para entrar aquí.");
      navigate("/login", { replace: true });
    }
  };

  useEffect(() => { loadSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSetup = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/pin/setup", { name, whatsapp });
      await checkAuth();
      if (data?.restored) toast.success("¡Bienvenido de nuevo! Recuperamos los datos que guardaste. 🎉");
      else toast.success("¡Listo! Bienvenido a tu plataforma.");
      navigate("/dashboard", { replace: true });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const isRena = pinType === "renalife";

  if (loading) {
    return <Center><Loader2 className="animate-spin text-purple-500" size={32} /></Center>;
  }

  return (
    <Center renalife={isRena}>
      <div className="w-full max-w-md">
        {isRena ? (
          <div className="text-center mb-6">
            <div className="font-renalife font-bold text-3xl text-[#F8FAF8]">
              RENALIFE <span className="text-[#FACC15]">MÉXICO</span>
            </div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-200/70 mt-1 font-mono">Expansión Renalife do Brasil</div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 mb-6 text-xl font-display font-black">
            MULTI<span className="text-purple-500">CONTACTOS</span>
          </div>
        )}
        <form
          onSubmit={saveSetup}
          className={`rounded-2xl border p-8 ${isRena ? "border-[#FACC15]/30 bg-[#02251c]/90 shadow-[0_0_60px_rgba(250,204,21,0.08)]" : "border-white/10 bg-[#0d0d0d] glow-purple"}`}
        >
          <div className="flex items-center gap-2 mb-1">
            {isRena ? <Leaf className="text-[#FACC15]" size={20} /> : <Ticket className="text-yellow-400" size={20} />}
            <h1 className={`font-bold text-2xl ${isRena ? "font-renalife text-[#F8FAF8]" : "font-display"}`}>
              {isRena ? "Bienvenido, distribuidor" : "Acceso con PIN"}
            </h1>
          </div>
          <p className={`text-sm mb-6 ${isRena ? "text-emerald-100/80" : "text-zinc-500"}`}>
            {isRena
              ? "Tus materiales ya están preparados: mensajes, imágenes, video, página de aterrizaje y calificación de prospectos con la marca RENALIFE. Solo dinos a dónde enviamos tus prospectos:"
              : "Con tu PIN tienes acceso a la plataforma completa para crear y publicar UNA campaña. Primero, tus datos:"}
          </p>
          <div className="space-y-4">
            <div>
              <Label className={`text-xs uppercase tracking-wider ${isRena ? "text-emerald-200/80" : "text-zinc-400"}`}>Nombre o pseudónimo</Label>
              <Input data-testid="pin-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Ana / LíderPro" required className={`mt-1.5 rounded-xl py-6 ${isRena ? "bg-[#011811] border-emerald-500/30 text-white focus-visible:ring-[#FACC15]" : "bg-black border-white/15 focus-visible:ring-purple-500"}`} />
            </div>
            <div>
              <Label className={`text-xs uppercase tracking-wider ${isRena ? "text-emerald-200/80" : "text-zinc-400"}`}>WhatsApp que recibe los prospectos (10 dígitos)</Label>
              <Input data-testid="pin-whatsapp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="5512345678" inputMode="numeric" required className={`mt-1.5 rounded-xl py-6 ${isRena ? "bg-[#011811] border-emerald-500/30 text-white focus-visible:ring-[#FACC15]" : "bg-black border-white/15 focus-visible:ring-purple-500"}`} />
              <p className={`text-xs mt-1 ${isRena ? "text-emerald-200/60" : "text-zinc-500"}`}>La IA enviará a este WhatsApp los prospectos mejor calificados.</p>
            </div>
            <Button
              data-testid="pin-setup-submit"
              type="submit"
              disabled={saving}
              className={`w-full font-bold rounded-xl py-6 ${isRena ? "bg-[#FACC15] hover:bg-yellow-300 text-[#022C22]" : "bg-yellow-400 hover:bg-yellow-300 text-black"}`}
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
              {isRena ? "Entrar a mi zona RENALIFE" : "Entrar a mi plataforma"}
            </Button>
          </div>
        </form>
      </div>
    </Center>
  );
}
